import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, RefreshCw, AlertTriangle, X, CheckCircle, FileText } from 'lucide-react';
import MainService from '../../services/MainService';
import { mapVehicleDropdownList, isApiSaveSuccess } from '../../utils/vehicleApiUtils';

const AddFine = () => {
  const { vehicleId, fineId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fine, setFine] = useState({
    Id: 0,
    VehicleId: vehicleId || '',
    DriverId: '',
    FineNumber: '',
    ViolationType: '',
    Amount: '',
    FineDate: '',
    Location: '',
    IsPaid: false,
    PaymentDate: '',
    Notes: ''
  });
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    loadVehicles();
    loadDrivers();
    if (fineId) {
      loadFine();
    }
  }, [fineId]);

  useEffect(() => {
    if (!fineId && vehicleId) {
      setFine((prev) => ({ ...prev, VehicleId: vehicleId }));
    }
  }, [vehicleId, fineId]);

  const loadVehicles = async () => {
    try {
      const response = await MainService.VehiclesInDropDown();
      setVehicles(mapVehicleDropdownList(response));
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setVehicles([]);
    }
  };

  const loadDrivers = async () => {
    try {
      const response = await MainService.DriversInDropDown();
      setDrivers(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error loading drivers:', err);
      setDrivers([]);
    }
  };

  const loadFine = async () => {
    try {
      setLoading(true);
      const row = await MainService.VehicleFineRecordById(fineId);
      if (row) {
        setFine({
          Id: row.Id ?? row.ID ?? 0,
          VehicleId: String(row.VehicleId ?? row.VehicleID ?? vehicleId ?? ''),
          DriverId: row.DriverId != null ? String(row.DriverId) : '',
          FineNumber: row.FineNumber || '',
          ViolationType: row.ViolationType || '',
          Amount: row.Amount != null ? String(row.Amount) : '',
          FineDate: row.FineDate ? String(row.FineDate).slice(0, 10) : '',
          Location: row.Location || '',
          IsPaid: !!row.IsPaid,
          PaymentDate: row.PaymentDate ? String(row.PaymentDate).slice(0, 10) : '',
          Notes: row.Notes || ''
        });
      }
    } catch (err) {
      console.error('Error loading fine:', err);
      setError('Failed to load fine record');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFine(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    if (!fine.FineNumber) {
      setError('Please enter fine number');
      return;
    }
    if (!fine.ViolationType) {
      setError('Please enter violation type');
      return;
    }
    if (!fine.Amount) {
      setError('Please enter amount');
      return;
    }
    if (!fine.FineDate) {
      setError('Please select fine date');
      return;
    }
    const amountNum = Number(fine.Amount);
    if (!Number.isFinite(amountNum)) {
      setError('Please enter a valid amount');
      return;
    }

    const vid = Number(fine.VehicleId || vehicleId);
    if (!vid) {
      setError('Please select vehicle');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const payload = {
        Id: Number(fine.Id) || 0,
        VehicleId: vid,
        DriverId: fine.DriverId === '' || fine.DriverId == null ? null : Number(fine.DriverId),
        FineNumber: fine.FineNumber,
        ViolationType: fine.ViolationType,
        Amount: amountNum,
        FineDate: fine.FineDate,
        Location: fine.Location || '',
        IsPaid: !!fine.IsPaid,
        PaymentDate: fine.PaymentDate || null,
        Notes: fine.Notes || ''
      };
      const response = await MainService.SaveVehicleFineRecord(payload);
      if (!isApiSaveSuccess(response)) {
        setError('Failed to save fine record');
        return;
      }
      setSuccess('Fine record saved successfully');
      setTimeout(() => {
        navigate(
          vehicleId
            ? `/home/vehicle-management/fine-records/${vehicleId}`
            : '/home/vehicle-management/fine-records'
        );
      }, 800);
    } catch (err) {
      console.error('Error saving fine:', err);
      setError('Failed to save fine record');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFine({
      Id: 0,
      VehicleId: vehicleId || '',
      DriverId: '',
      FineNumber: '',
      ViolationType: '',
      Amount: '',
      FineDate: '',
      Location: '',
      IsPaid: false,
      PaymentDate: '',
      Notes: ''
    });
    setError('');
    setSuccess('');
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <FileText className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">
              {fineId ? 'Edit Fine' : 'Add Fine'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>Save</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 ml-8">
          {fineId ? 'Update fine record' : 'Create a new fine record'}
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Vehicle */}
          {!vehicleId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle <span className="text-red-500">*</span>
              </label>
              <select
                name="VehicleId"
                value={fine.VehicleId}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
              >
                <option value="">Select Vehicle</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.Id} value={vehicle.Id}>
                    {vehicle.PlateNumber || vehicle.Number || vehicle.Name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Driver */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Driver</label>
            <select
              name="DriverId"
              value={fine.DriverId}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            >
              <option value="">Select Driver</option>
              {drivers.map(driver => (
                <option key={driver.Id} value={driver.Id}>
                  {driver.Name || driver.EmployeeName}
                </option>
              ))}
            </select>
          </div>

          {/* Fine Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fine Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="FineNumber"
              value={fine.FineNumber}
              onChange={handleInputChange}
              placeholder="Enter fine number"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Violation Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Violation Type <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="ViolationType"
              value={fine.ViolationType}
              onChange={handleInputChange}
              placeholder="Enter violation type"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="Amount"
              value={fine.Amount}
              onChange={handleInputChange}
              placeholder="Enter amount"
              step="0.01"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Fine Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fine Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="FineDate"
              value={fine.FineDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!fine.FineDate ? 'text-gray-400' : 'text-gray-900'}`}
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <input
              type="text"
              name="Location"
              value={fine.Location}
              onChange={handleInputChange}
              placeholder="Enter location"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Is Paid */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="IsPaid"
              checked={fine.IsPaid}
              onChange={handleInputChange}
              className="w-4 h-4 text-[#46955A] border-gray-300 rounded focus:ring-[#46955A]"
            />
            <label className="ml-2 text-sm font-medium text-gray-700">Paid</label>
          </div>

          {/* Payment Date */}
          {fine.IsPaid && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
              <input
                type="date"
                name="PaymentDate"
                value={fine.PaymentDate}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!fine.PaymentDate ? 'text-gray-400' : 'text-gray-900'}`}
              />
            </div>
          )}

          {/* Notes */}
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="Notes"
              value={fine.Notes}
              onChange={handleInputChange}
              placeholder="Enter additional notes"
              rows="3"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFine;

